/**
 * TenantCharts Component
 * Two side-by-side line charts: Users over time and Tokens over time per tenant
 */

import { useMemo } from 'react';
import { Card, Row, Col, Empty, Typography } from 'antd';
import { Line } from '@ant-design/charts';
import { useLanguage } from '@/hooks/useLanguage';
import type { TimeseriesPoint } from '../types/insights';

const { Title } = Typography;

interface TenantChartsProps {
  timeseries: TimeseriesPoint[];
  loading: boolean;
}

export function TenantCharts({ timeseries, loading }: TenantChartsProps) {
  const { t } = useLanguage();

  const usersChartData = useMemo(() => {
    return timeseries
      .filter(p => p.users !== undefined)
      .map(p => ({
        date: p.date,
        tenant: p.tenant_name || 'Unknown',
        users: p.users ?? 0,
      }));
  }, [timeseries]);

  const tokensChartData = useMemo(() => {
    return timeseries
      .filter(p => p.input_tokens !== undefined || p.output_tokens !== undefined)
      .map(p => ({
        date: p.date,
        tenant: p.tenant_name || 'Unknown',
        tokens: (p.input_tokens ?? 0) + (p.output_tokens ?? 0),
      }));
  }, [timeseries]);

  const usersConfig = {
    data: usersChartData,
    xField: 'date',
    yField: 'users',
    colorField: 'tenant',
    smooth: true,
    point: { size: 3 },
    legend: { position: 'top-right' as const },
    height: 220,
  };

  const tokensConfig = {
    data: tokensChartData,
    xField: 'date',
    yField: 'tokens',
    colorField: 'tenant',
    smooth: true,
    point: { size: 3 },
    legend: { position: 'top-right' as const },
    height: 220,
  };

  return (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col xs={24} md={12}>
        <Card
          title={
            <Title level={5} style={{ margin: 0 }}>
              {t('insights.tenantCharts.usersOverTime')}
            </Title>
          }
          loading={loading}
          bordered={false}
          style={{ border: '1px solid var(--color-border, #e8e8e8)' }}
        >
          {!loading && usersChartData.length === 0 ? (
            <Empty description={t('insights.tenantCharts.noData')} />
          ) : (
            <Line {...usersConfig} />
          )}
        </Card>
      </Col>
      <Col xs={24} md={12}>
        <Card
          title={
            <Title level={5} style={{ margin: 0 }}>
              {t('insights.tenantCharts.tokensOverTime')}
            </Title>
          }
          loading={loading}
          bordered={false}
          style={{ border: '1px solid var(--color-border, #e8e8e8)' }}
        >
          {!loading && tokensChartData.length === 0 ? (
            <Empty description={t('insights.tenantCharts.noData')} />
          ) : (
            <Line {...tokensConfig} />
          )}
        </Card>
      </Col>
    </Row>
  );
}
